import ErrorNote from './ErrorNote'
import { useState } from 'react'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { AlertTriangle, Check, CheckCircle2, DoorOpen, Undo2, User, Users, Wrench } from 'lucide-react'
import {
  clearConflictReview,
  collisionTokens,
  fetchConflicts,
  reviewConflict,
  type Conflict,
  type ConflictCollision,
  type ConflictVerdict,
} from '../api/bookings'
import { addDays, formatFullDate, formatRange, startOfWeek, today } from '../api/calendar'
import CalendarDatePicker from './CalendarDatePicker'
import ConflictDetail from './ConflictDetail'

type Kind = Conflict['kind']

/**
 * How each kind of collision reads on a row (D-609).
 *
 * `asked`: <i>"seems like some of the conflicts are showing up twice, once
 * under teacher once under room, not sure what is the right way to display them
 * but its kind of confusing?"</i> They were the same two classes in the same
 * half hour, listed under two headings, and the screen offered no way to tell
 * that from two separate problems.
 *
 * <b>Badges rather than picking one kind to show.</b> The three are not
 * interchangeable and the duplicate is not noise: a pair can clash on a dancer
 * without clashing on the room, which is a different conversation and a
 * different fix. Hiding one would throw away why it matters.
 */
const BADGES: Record<Kind, { label: string; tone: string; Icon: typeof User }> = {
  Dancer: { label: 'Dancer', tone: 'border-crit/30 bg-crit-bg text-crit', Icon: Users },
  Teacher: { label: 'Teacher', tone: 'border-warn/30 bg-warn-bg text-warn', Icon: User },
  Room: { label: 'Room', tone: 'border-accent/30 bg-accent-soft text-accent-2', Icon: DoorOpen },
}

/** The order the kinds read in, so two rows never disagree about it. */
const KIND_ORDER: Kind[] = ['Dancer', 'Teacher', 'Room']

/**
 * Everything the pair collides on, in a fixed order.
 *
 * Falls back to the row's own headline for a conflict from a server that does
 * not send the list - the screen must never render a row with no badge at all,
 * which would read as a clash over nothing.
 */
export function collisionsOf(row: Conflict): ConflictCollision[] {
  const all: ConflictCollision[] = row.collides?.length
    ? [...row.collides]
    : [{ kind: row.kind, subject: row.subject, subjectId: row.subjectId }]

  return all.sort((a, b) => KIND_ORDER.indexOf(a.kind) - KIND_ORDER.indexOf(b.kind))
}

/**
 * One clash, however many nights of the week it happens on.
 *
 * The same two classes overlap every week they both run, so the raw list
 * repeats. Collapsing on the key means a verdict is given once, against the
 * arrangement rather than against a Tuesday (D-150).
 */
export type ConflictRow = Conflict & { dates: string[] }

export function collapseByKey(conflicts: Conflict[]): ConflictRow[] {
  const rows = new Map<string, ConflictRow>()

  for (const c of conflicts) {
    const existing = rows.get(c.key)
    if (existing) {
      if (!existing.dates.includes(c.date)) existing.dates.push(c.date)
      continue
    }
    rows.set(c.key, { ...c, dates: [c.date] })
  }

  /*
   * <b>Sorted, because `dates[0]` is now a navigation target</b> (D-567).
   *
   * The dates arrive in whatever order the detector produced them, which is
   * first-appearance order over a query with no `ORDER BY` — so "the earliest
   * night" was arrival order wearing the right answer's clothes. It cost
   * nothing while the first date was only a label; it picks which night the
   * strip draws and which day the link opens now, and both should be the
   * first one this arrangement clashes on.
   */
  for (const row of rows.values()) row.dates.sort()

  return [...rows.values()]
}

/**
 * Everything double-booked this week, and what the studio decided about it.
 *
 * A list, not a grid. Tonya's question is "does anything clash?", and the answer
 * is normally a handful of rows — so this scales to any number of dancers,
 * where a column-per-student grid would not survive three hundred of them
 * (D-81).
 *
 * Dancers lead because they are the ones nobody currently checks: teacher
 * clashes are caught by the per-teacher grid she already maintains by hand, and
 * a dancer clash is discovered by a parent standing in a corridor.
 *
 * **Judging is one click.** The detector cannot tell a dancer leaving technique
 * early for her solo from a dancer double-booked by mistake, and only the studio
 * can — so the screen asks rather than guesses (D-150). Asking has to cost
 * almost nothing or it will not get done; the note is offered afterwards,
 * because the reasoning is worth more than the verdict but should never be the
 * price of giving one.
 */
export default function ConflictsView() {
  const [anchor, setAnchor] = useState(today())
  const [showSettled, setShowSettled] = useState(false)
  /*
   * Which row is open (D-567). Component state rather than the URL, unlike
   * the class record one screen over: a conflict has no id of its own - its
   * key is built from the two classes that clash (D-150, D-609) - and it
   * exists only for as long as the schedule says so. An address for it would
   * be one that stops resolving without anything having gone wrong.
   */
  const [looking, setLooking] = useState<ConflictRow | null>(null)

  const weekStart = startOfWeek(anchor)
  const weekEnd = addDays(weekStart, 6)

  const client = useQueryClient()
  const { data, isPending, isError, error } = useQuery({
    queryKey: ['conflicts', weekStart, weekEnd],
    queryFn: () => fetchConflicts(weekStart, weekEnd),
  })

  const judge = useMutation({
    mutationFn: (input: {
      key: string
      verdict: ConflictVerdict
      note?: string
      summary?: string
      collides?: string[]
    }) => reviewConflict(input),
    onSuccess: () => client.invalidateQueries({ queryKey: ['conflicts'] }),
  })

  const undo = useMutation({
    mutationFn: (key: string) => clearConflictReview(key),
    onSuccess: () => client.invalidateQueries({ queryKey: ['conflicts'] }),
  })

  const rows = collapseByKey(data ?? [])

  // Three states, three places. Unanswered is the work; needs-fixing is the
  // list she is keeping; expected is filed away but never thrown away.
  const open = rows.filter((r) => !r.verdict)
  const fixing = rows.filter((r) => r.verdict === 'NeedsFixing')
  const settled = rows.filter((r) => r.verdict === 'Expected')

  /*
   * One list, not three (D-609). Dancers still lead - they are the ones nobody
   * currently checks, since a teacher clash is caught by the grid she keeps by
   * hand and a dancer clash is found by a parent standing in a corridor - but
   * they lead as an ordering rather than as a heading, because a pair that
   * clashes on a dancer *and* the room belongs in both headings and is one
   * problem.
   */
  const waiting = [...open].sort(
    (a, b) => KIND_ORDER.indexOf(a.kind) - KIND_ORDER.indexOf(b.kind),
  )

  const act = (row: ConflictRow, verdict: ConflictVerdict, note?: string) =>
    judge.mutate({
      key: row.key,
      verdict,
      note,
      summary: row.description,
      // What she is answering about, so a collision this pair picks up later
      // asks again rather than arriving pre-excused (D-609).
      collides: collisionTokens(row),
    })

  return (
    <div className="page-shell px-5 py-8">
      {/*
        No back link (D-594). D-353's control is for screens the rail does not
        carry, and this has been a rail destination since D-585 - a way out
        of a place that is on the rail is the fourth navigation D-353 said
        not to build.
      */}
      <header className="mb-5">
        <p className="text-[11px] font-bold tracking-[0.14em] text-accent uppercase">Conflicts</p>
        <CalendarDatePicker
          label={formatRange(weekStart, weekEnd)}
          anchor={anchor}
          view="week"
          onPick={setAnchor}
        />
        {data && (
          <p className="mt-1 text-sm text-ink-3">
            {open.length === 0
              ? 'Nothing new to look at this week.'
              : `${open.length} to look at`}
            {fixing.length > 0 && ` · ${fixing.length} to fix`}
            {settled.length > 0 && ` · ${settled.length} marked as expected`}
          </p>
        )}
      </header>

      {isPending && <p className="text-sm text-ink-3">Checking the week...</p>}

      {isError && (
        <ErrorNote boxed>
          {(error as Error).message}
        </ErrorNote>
      )}

      {judge.isError && (
        <p className="mb-4 rounded-xl border border-crit bg-crit-bg px-4 py-3 text-sm text-crit">
          {(judge.error as Error).message}
        </p>
      )}

      {data && rows.length === 0 && (
        <div className="flex items-center gap-3 rounded-2xl border border-line bg-surface px-5 py-6">
          <CheckCircle2 size={22} strokeWidth={2} className="shrink-0 text-good" />
          <div>
            <p className="text-[14px] font-semibold">All clear</p>
            <p className="mt-0.5 text-[13px] text-ink-3">
              No teacher, room, or dancer is scheduled in two places this week.
            </p>
          </div>
        </div>
      )}

      <div className="flex flex-col gap-5">
        {/*
          Anything she has said is a real problem comes first, above the
          unanswered ones. It is the only part of this screen that is a to-do
          list rather than a question.
        */}
        {fixing.length > 0 && (
          <section className="overflow-hidden rounded-2xl border border-crit bg-surface">
            <header className="flex items-start gap-2.5 border-b border-crit bg-crit-bg px-4 py-3">
              <Wrench size={16} strokeWidth={2.5} className="mt-0.5 shrink-0 text-crit" />
              <div className="flex-1">
                <h2 className="text-[13.5px] font-bold text-crit">
                  Still to fix
                  <span className="tabular ml-2 font-semibold">{fixing.length}</span>
                </h2>
                <p className="mt-0.5 text-[12px] text-ink-2">
                  Marked as a real problem. Stays here until the schedule changes or the verdict does.
                </p>
              </div>
            </header>
            <ul className="flex flex-col divide-y divide-line">
              {fixing.map((row) => (
                <ConflictItem
                  key={row.key}
                  row={row}
                  onJudge={act}
                  onUndo={() => undo.mutate(row.key)}
                  onOpen={() => setLooking(row)}
                  busy={judge.isPending || undo.isPending}
                />
              ))}
            </ul>
          </section>
        )}

        {waiting.length > 0 && (
          <section className="overflow-hidden rounded-2xl border border-line bg-surface">
            <header className="flex items-start gap-2.5 border-b border-line bg-surface-2 px-4 py-3">
              <AlertTriangle size={16} strokeWidth={2.5} className="mt-0.5 shrink-0 text-ink-2" />
              <div className="flex-1">
                <h2 className="text-[13.5px] font-bold">
                  To look at
                  <span className="tabular ml-2 font-semibold text-crit">{waiting.length}</span>
                </h2>
                <p className="mt-0.5 text-[12px] text-ink-3">
                  One row per pair of classes. The badges say what each pair breaks.
                </p>
              </div>
            </header>

            <ul className="flex flex-col divide-y divide-line">
              {waiting.map((row) => (
                <ConflictItem
                  key={row.key}
                  row={row}
                  onJudge={act}
                  onUndo={() => undo.mutate(row.key)}
                  onOpen={() => setLooking(row)}
                  busy={judge.isPending || undo.isPending}
                />
              ))}
            </ul>
          </section>
        )}

        {/*
          Filed, not deleted. Somebody will eventually ask why a clash stopped
          being reported, and the answer — with who said so and why — has to be
          somewhere she can reach.
        */}
        {settled.length > 0 && (
          <section className="overflow-hidden rounded-2xl border border-line bg-surface">
            <button
              type="button"
              onClick={() => setShowSettled((v) => !v)}
              className="flex w-full items-center gap-2.5 bg-surface-2 px-4 py-3 text-left hover:bg-surface"
            >
              <CheckCircle2 size={16} strokeWidth={2.5} className="shrink-0 text-good" />
              <span className="text-[13.5px] font-bold">
                Marked as expected
                <span className="tabular ml-2 font-semibold text-ink-3">{settled.length}</span>
              </span>
              <span className="ml-auto text-[12px] font-semibold text-ink-3">
                {showSettled ? 'Hide' : 'Show'}
              </span>
            </button>

            {showSettled && (
              <ul className="flex flex-col divide-y divide-line border-t border-line">
                {settled.map((row) => (
                  <ConflictItem
                    key={row.key}
                    row={row}
                    onJudge={act}
                    onUndo={() => undo.mutate(row.key)}
                  onOpen={() => setLooking(row)}
                    busy={judge.isPending || undo.isPending}
                  />
                ))}
              </ul>
            )}
          </section>
        )}
      </div>

      {looking && <ConflictDetail row={looking} onClose={() => setLooking(null)} />}
    </div>
  )
}

function ConflictItem({
  row,
  onJudge,
  onUndo,
  onOpen,
  busy,
}: {
  row: ConflictRow
  onJudge: (row: ConflictRow, verdict: ConflictVerdict, note?: string) => void
  onUndo: () => void
  onOpen: () => void
  busy: boolean
}) {
  const [writing, setWriting] = useState(false)
  const [note, setNote] = useState(row.note ?? '')

  // A note explains a verdict, so it keeps the one already given rather than
  // implying a new one.
  const save = () => {
    if (row.verdict) onJudge(row, row.verdict, note.trim())
    setWriting(false)
  }

  return (
    <li className="px-4 py-3">
      {/*
        <b>The row opens</b> (D-567). `asked`: <i>"only being able to say its
        fine or needs fixing makes that page difficult to manage."</i> A verdict
        is a judgement and nothing here let her look at what she was judging.
        <b>The whole row, not a separate control</b> — she is going down a list
        deciding about things, so the thing itself is the target.
        <b>A button rather than a link</b>, unlike the class rows one screen
        over: this opens a dialog over the queue rather than going somewhere,
        and where it would go depends on the kind, which is why the dialog is
        the one place that decides.
      */}
      <button
        type="button"
        onClick={onOpen}
        aria-haspopup="dialog"
        className="-mx-1.5 block w-full rounded-lg px-1.5 py-1 text-left hover:bg-surface-2"
      >
        <span className="flex items-start gap-2">
          {row.verdict === 'Expected' ? (
            <Check size={13} strokeWidth={3} className="mt-1 shrink-0 text-good" />
          ) : (
            <AlertTriangle size={13} strokeWidth={2.5} className="mt-1 shrink-0 text-crit" />
          )}
          {/*
            Every collision, not just the headline one (D-609). A pair that
            breaks on a teacher and a room used to be two rows under two
            headings; it is one row saying both.
          */}
          <span className="flex flex-wrap items-center gap-1.5">
            {collisionsOf(row).map((c) => {
              const badge = BADGES[c.kind]
              return (
                <span
                  key={`${c.kind}:${c.subjectId}`}
                  className={`inline-flex items-center gap-1 rounded-md border px-1.5 py-0.5 text-[11.5px] font-semibold ${badge.tone}`}
                >
                  <badge.Icon size={11} strokeWidth={2.5} className="shrink-0" />
                  {badge.label} &middot; {c.subject}
                </span>
              )
            })}
          </span>
          <span className="ml-auto shrink-0 pt-0.5 text-[12px] text-ink-3">
            {formatFullDate(row.dates[0])}
            {row.dates.length > 1 && ` +${row.dates.length - 1}`}
          </span>
        </span>

        <span className="mt-1 block pl-[21px] text-[12.5px] wrap-anywhere text-ink-2">
          <strong>{row.first}</strong> and <strong>{row.second}</strong> overlap{' '}
          <span className="tabular">{row.overlap}</span>
        </span>
      </button>

      {row.note && !writing && (
        <p className="mt-1.5 pl-[21px] text-[12.5px] wrap-anywhere text-ink-3 italic">
          &ldquo;{row.note}&rdquo;
        </p>
      )}

      {writing ? (
        <div className="mt-2 flex flex-wrap items-center gap-2 pl-[21px]">
          <input
            autoFocus
            value={note}
            onChange={(e) => setNote(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === 'Enter') save()
              if (e.key === 'Escape') setWriting(false)
            }}
            placeholder="Why is this the way it is?"
            className="min-w-[240px] flex-1 rounded-lg border border-line-2 bg-surface-2 px-3 py-2 text-[13px] text-ink placeholder:text-ink-3 focus:border-accent focus:outline-none"
          />
          <button
            type="button"
            onClick={save}
            disabled={busy}
            className="rounded-lg border border-line-2 bg-surface px-3 py-2 text-[13px] font-semibold text-ink-2 hover:border-ink-3 disabled:opacity-50"
          >
            Save
          </button>
        </div>
      ) : (
        <div className="mt-2 flex flex-wrap items-center gap-2 pl-[21px]">
          {row.verdict ? (
            <>
              <span className="text-[12px] font-semibold text-ink-3">
                {row.verdict === 'Expected' ? 'Marked as expected' : 'Marked as needing fixing'}
              </span>
              <button
                type="button"
                onClick={onUndo}
                disabled={busy}
                className="ml-auto flex items-center gap-1.5 rounded-lg border border-line-2 bg-surface px-3 py-1.5 text-[12.5px] font-semibold text-ink-2 hover:border-ink-3 disabled:opacity-50"
              >
                <Undo2 size={13} strokeWidth={2.5} />
                Undo
              </button>
            </>
          ) : (
            <>
              <button
                type="button"
                onClick={() => onJudge(row, 'Expected')}
                disabled={busy}
                className="flex items-center gap-1.5 rounded-lg border border-line-2 bg-surface px-3 py-1.5 text-[12.5px] font-semibold text-ink-2 hover:border-good hover:text-good disabled:opacity-50"
              >
                <Check size={13} strokeWidth={3} />
                That&rsquo;s fine
              </button>
              <button
                type="button"
                onClick={() => onJudge(row, 'NeedsFixing')}
                disabled={busy}
                className="flex items-center gap-1.5 rounded-lg border border-line-2 bg-surface px-3 py-1.5 text-[12.5px] font-semibold text-ink-2 hover:border-crit hover:text-crit disabled:opacity-50"
              >
                <Wrench size={13} strokeWidth={2.5} />
                Needs fixing
              </button>
            </>
          )}
          {/* Offered only once a verdict exists — a note is the reason for one. */}
          {row.verdict && (
            <button
              type="button"
              onClick={() => setWriting(true)}
              className="text-[12.5px] font-semibold text-ink-3 underline underline-offset-2 hover:text-ink-2"
            >
              {row.note ? 'Edit note' : 'Add a note'}
            </button>
          )}
        </div>
      )}
    </li>
  )
}
